import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import UsersGrowthChart from '../../Components/Dashboard/charts/UsersGrowthChart';
import ProgramManagementCard from '../../Components/Dashboard/ProgramManagementCard';
import VolunteerManagementCard from '../../Components/Dashboard/VolunteerManagementCard';
import ConversionFunnelCard from '../../Components/Dashboard/ConversionFunnelCard';
import SectionCard from '../../Components/Table/SectionCard';
import useDashboardData from '../../hooks/useDashboardData';
import useCohortDashboard from '../../hooks/useCohortDashboard';
import useClpReach from '../../hooks/useClpReach';
import api from '../../utils/api';

const formatNumber = (n) => Number(n || 0).toLocaleString('id-ID');

const PROGRAM_META = [
  { key: 'praktikum', label: 'Praktikum', color: '#0eb9d1' },
  { key: 'msib', label: 'MSIB', color: '#6366f1' },
  { key: 'mandiri', label: 'Mandiri', color: '#f59e0b' },
  { key: 'volunteer', label: 'Volunteer', color: '#e91e63' },
  { key: 'bumdes', label: 'BUMDes', color: '#16a34a' },
];

/** Titik kecil per program di tiap item dropdown — preview cepat program mana yang ada datanya di periode itu, tanpa perlu buka satu-satu. */
function AvailabilityDots({ batches }) {
  return (
    <span className="d-inline-flex gap-1 ms-2">
      {PROGRAM_META.map((p) => (
        <span
          key={p.key}
          title={p.label}
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            display: 'inline-block',
            backgroundColor: batches?.[p.key] ? p.color : '#e5e7eb',
          }}
        ></span>
      ))}
    </span>
  );
}

/**
 * Ikon info klik-untuk-buka (bukan native `title`/hover tooltip) — tooltip hover
 * gampang kelewat (invisible di layar sentuh, butuh hover yang tidak semua orang
 * lakukan). Klik lebih pasti kebaca, dan label utama tetap membawa qualifier-nya
 * sendiri (mis. "(Semua Status)") supaya orang yang TIDAK klik pun sudah dapat sinyal
 * yang benar, bukan cuma "Total" yang menyesatkan.
 */
function InfoTip({ text }) {
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <span className="position-relative d-inline-block" ref={wrapperRef}>
      <button
        type="button"
        className="info-tip-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label="Penjelasan angka ini"
      >
        <i className="bi bi-info-circle"></i>
      </button>
      {open && (
        <div className="info-tip-popover shadow-sm">
          {text}
        </div>
      )}
    </span>
  );
}

/** Kartu KPI ringkas untuk zona tren — dipakai 3x, skeleton-nya konsisten dengan kartu program. */
function StatCard({ label, value, sub, loading, info }) {
  return (
    <div className="card shadow-sm border-0 rounded-4 p-3 h-100">
      <div className="d-flex align-items-center gap-1 text-muted small fw-medium mb-2">
        <span>{label}</span>
        {info && <InfoTip text={info} />}
      </div>
      {loading ? (
        <>
          <div className="skeleton-block rounded-2 mb-2" style={{ height: 28, width: '60%' }}></div>
          <div className="skeleton-block rounded-2" style={{ height: 14, width: '40%' }}></div>
        </>
      ) : (
        <>
          <div className="h3 fw-bold mb-1">{value}</div>
          {sub}
        </>
      )}
    </div>
  );
}

/** Tile ringkas untuk zona "Jangkauan CLP" — varian StatCard dengan ikon di atas, dipakai 7x dalam satu baris. */
function ReachTile({ icon, label, value, loading, info }) {
  return (
    <div className="card shadow-sm border-0 rounded-4 p-3 h-100 text-center">
      <i className={`bi ${icon} text-warning mb-2`} style={{ fontSize: '1.1rem' }}></i>
      {loading ? (
        <>
          <div className="skeleton-block rounded-2 mb-2 mx-auto" style={{ height: 24, width: '50%' }}></div>
          <div className="skeleton-block rounded-2 mx-auto" style={{ height: 12, width: '70%' }}></div>
        </>
      ) : (
        <>
          <div className="h4 fw-bold mb-1">{value}</div>
          <div className="d-flex align-items-center justify-content-center gap-1 text-muted extra-small fw-semibold text-uppercase">
            <span>{label}</span>
            {info && <InfoTip text={info} />}
          </div>
        </>
      )}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cohorts, setCohorts] = React.useState([]);
  const [selectedCohortId, setSelectedCohortIdState] = React.useState(
    () => searchParams.get('cohort') || null,
  );
  const [loadingCohorts, setLoadingCohorts] = React.useState(true);
  const [cohortSearch, setCohortSearch] = React.useState('');

  // Ganti cohort aktif + sinkronkan ke query param ?cohort= supaya periode
  // yang sedang dilihat bisa di-bookmark/dibagikan, dan tombol back/forward
  // browser jalan sebagaimana mestinya.
  const setSelectedCohortId = (id) => {
    setSelectedCohortIdState(id);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set('cohort', id);
        else next.delete('cohort');
        return next;
      },
      { replace: true },
    );
  };

  const { analytics, loading } = useDashboardData();
  const { data: clpReach, loading: clpReachLoading } = useClpReach();

  const selectedCohort =
    cohorts.find((c) => String(c.cohortId) === String(selectedCohortId)) || null;
  const { management, funnel, availability, loading: cohortLoading } = useCohortDashboard(selectedCohort);

  useEffect(() => {
    const fetchCohorts = async () => {
      try {
        const data = await api.analyticsCohortOptions();
        setCohorts(data || []);
        // sudah terurut DESC by cohortId, jadi item pertama = periode terbaru
        if (data && data.length > 0) {
          const fromUrl = searchParams.get('cohort');
          const match = fromUrl && data.find((c) => String(c.cohortId) === String(fromUrl));
          setSelectedCohortId(match ? match.cohortId : data[0].cohortId);
        }
      } catch (err) {
        console.error('Error fetching cohort options:', err);
      } finally {
        setLoadingCohorts(false);
      }
    };
    fetchCohorts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString('id-ID', { month: 'short', year: 'numeric' }) : null);
  const cohortPeriod = selectedCohort && selectedCohort.dateStart
    ? `${formatDate(selectedCohort.dateStart)} - ${formatDate(selectedCohort.dateEnd)}`
    : null;

  // Daftar sudah terurut DESC (terbaru dulu), jadi index+1 = periode lebih lama, index-1 = lebih baru.
  const cohortIndex = cohorts.findIndex(
    (c) => String(c.cohortId) === String(selectedCohortId),
  );
  const goToOlderCohort = () => {
    if (cohortIndex >= 0 && cohortIndex < cohorts.length - 1) {
      setSelectedCohortId(cohorts[cohortIndex + 1].cohortId);
    }
  };
  const goToNewerCohort = () => {
    if (cohortIndex > 0) {
      setSelectedCohortId(cohorts[cohortIndex - 1].cohortId);
    }
  };
  const filteredCohorts = cohorts.filter((c) =>
    c.label.toLowerCase().includes(cohortSearch.trim().toLowerCase())
  );

  // -----------------------
  // Akumulasi all-time + delta tahun terakhir.
  // `analytics` berisi 1 baris per tahun registrasi (dari countUsersTotal di
  // backend); beberapa baris punya `year: null` (created_at tidak lengkap) —
  // tetap ikut dijumlah ke total, tapi jangan dipakai sebagai "tahun terakhir"
  // di sub-teks supaya tidak menulis "baru di null".
  // -----------------------
  const totalUsersAllTime = analytics.reduce((sum, a) => sum + (a.count_users || 0), 0);
  const totalClpAllTime = analytics.reduce((sum, a) => sum + (a.count_clp_users || 0), 0);
  const ratioAllTime = totalUsersAllTime
    ? ((totalClpAllTime / totalUsersAllTime) * 100).toFixed(1)
    : 0;

  const yearsWithData = analytics.filter((a) => a.year != null);
  const latestYear = yearsWithData.length > 0
    ? yearsWithData[yearsWithData.length - 1]
    : null;

  const deltaBadge = (value, label) => (
    <span className="small fw-semibold text-muted">
      <i className="bi bi-plus-circle-fill text-success me-1"></i>
      {formatNumber(value)} {label} di {latestYear?.year || '-'}
    </span>
  );

  return (
    <div className="container-fluid p-0">
      {/* Page header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div className="d-flex align-items-center gap-3">
          <div
            className="bg-primary bg-opacity-10 d-flex align-items-center justify-content-center rounded-3"
            style={{ width: '40px', height: '40px' }}
          >
            <i className="bi bi-speedometer2 text-primary fs-5"></i>
          </div>
          <div>
            <h1 className="h5 fw-bold mb-0 text-dark">Dashboard</h1>
            <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>
              Ringkasan operasional program dan pertumbuhan platform.
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm rounded-pill px-3"
          onClick={() => navigate('/dashboard/insights')}
        >
          <i className="bi bi-graph-up-arrow me-1"></i>
          Insight Peserta
        </button>
      </div>

      {/* Zona 1: Operasional Taktis */}
      <SectionCard
        icon="bi-gear-fill"
        iconColor="primary"
        eyebrow="Operasional Taktis"
        title="Program Management"
        actions={
          <div className="d-flex align-items-center gap-1">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm rounded-circle p-0 d-inline-flex align-items-center justify-content-center"
              style={{ width: 30, height: 30 }}
              onClick={goToOlderCohort}
              disabled={cohortIndex < 0 || cohortIndex >= cohorts.length - 1}
              aria-label="Periode sebelumnya"
              title="Periode sebelumnya"
            >
              <i className="bi bi-chevron-left small"></i>
            </button>

            <div className="dropdown">
              <button
                className="btn btn-outline-secondary btn-sm rounded-pill px-3 dropdown-toggle"
                type="button"
                id="cohortDropdown"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                aria-haspopup="listbox"
              >
                <i className="bi bi-calendar-range me-1"></i>
                <span className="text-muted me-1">Periode:</span>
                <strong>{selectedCohort ? selectedCohort.label : (loadingCohorts ? 'Memuat...' : 'Pilih Periode')}</strong>
              </button>
              <ul className="dropdown-menu dropdown-menu-end shadow border-0 rounded-4 mt-2 p-0" aria-labelledby="cohortDropdown" role="listbox" style={{ width: 280 }}>
                {!loadingCohorts && cohorts.length > 6 && (
                  <li className="px-2 pt-2 pb-1">
                    <input
                      type="text"
                      className="form-control form-control-sm rounded-pill"
                      placeholder="Cari periode..."
                      value={cohortSearch}
                      onChange={(e) => setCohortSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </li>
                )}
                <div style={{ maxHeight: '320px', overflowY: 'auto', overflowX: 'hidden' }}>
                  {loadingCohorts ? (
                    <li><span className="dropdown-item-text small">Loading periode...</span></li>
                  ) : filteredCohorts.length > 0 ? (
                    filteredCohorts.map((cohort, idx) => (
                      <li key={cohort.cohortId} role="option" aria-selected={String(cohort.cohortId) === String(selectedCohortId)}>
                        <button
                          className={`dropdown-item small py-2 px-3 ${String(cohort.cohortId) === String(selectedCohortId) ? 'active' : ''}`}
                          onClick={() => setSelectedCohortId(cohort.cohortId)}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>
                              {cohort.label}
                              {idx === 0 && !cohortSearch && (
                                <span className="badge bg-success-subtle text-success rounded-pill ms-2 extra-small fw-semibold">Terbaru</span>
                              )}
                            </span>
                            <AvailabilityDots batches={cohort.batches} />
                          </div>
                          <span className="d-block extra-small text-muted mt-1">
                            {formatDate(cohort.dateStart) || 'Tanggal tidak tersedia'} - {formatDate(cohort.dateEnd) || ''}
                          </span>
                        </button>
                      </li>
                    ))
                  ) : (
                    <li><span className="dropdown-item-text small">{cohorts.length > 0 ? 'Tidak ditemukan' : 'Belum ada periode'}</span></li>
                  )}
                </div>
              </ul>
            </div>

            <button
              type="button"
              className="btn btn-outline-secondary btn-sm rounded-circle p-0 d-inline-flex align-items-center justify-content-center"
              style={{ width: 30, height: 30 }}
              onClick={goToNewerCohort}
              disabled={cohortIndex <= 0}
              aria-label="Periode berikutnya"
              title="Periode berikutnya"
            >
              <i className="bi bi-chevron-right small"></i>
            </button>
          </div>
        }
      >
        <p className="text-muted small mb-3">
          <i className="bi bi-info-circle me-1"></i>
          {cohortPeriod
            ? `Data Praktikum, MSIB, Mandiri, Volunteer, dan BUMDes pada periode ${cohortPeriod} — tiap program dipetakan ke batch masing-masing secara otomatis.`
            : 'Tiap program dipetakan ke batch masing-masing secara otomatis karena penomoran batch berbeda per program.'}
        </p>

        <div className="row g-3 mb-3">
          <div className="col-6 col-lg-4 col-xl">
            <ProgramManagementCard
              title="Praktikum"
              data={management?.praktikum}
              icon="bi-journal-code"
              baseColor="#0eb9d1"
              loading={cohortLoading}
              batchLabel={selectedCohort?.label}
              unavailable={!cohortLoading && !availability.praktikum}
            />
          </div>
          <div className="col-6 col-lg-4 col-xl">
            <ProgramManagementCard
              title="MSIB"
              data={management?.msib}
              icon="bi-briefcase"
              baseColor="#6366f1"
              loading={cohortLoading}
              batchLabel={selectedCohort?.label}
              unavailable={!cohortLoading && !availability.msib}
            />
          </div>
          <div className="col-6 col-lg-4 col-xl">
            <ProgramManagementCard
              title="Mandiri"
              data={management?.mandiri}
              icon="bi-star"
              baseColor="#f59e0b"
              loading={cohortLoading}
              batchLabel={selectedCohort?.label}
              unavailable={!cohortLoading && !availability.mandiri}
            />
          </div>
          <div className="col-6 col-lg-4 col-xl">
            <VolunteerManagementCard
              data={management?.volunteer}
              loading={cohortLoading}
              batchLabel={selectedCohort?.label}
              unavailable={!cohortLoading && !availability.volunteer}
            />
          </div>
          <div className="col-6 col-lg-4 col-xl">
            <ProgramManagementCard
              title="BUMDes"
              data={management?.bumdes}
              icon="bi-houses"
              baseColor="#16a34a"
              loading={cohortLoading}
              batchLabel={selectedCohort?.label}
              unavailable={!cohortLoading && !availability.bumdes}
            />
          </div>
        </div>

        <ConversionFunnelCard data={funnel} loading={cohortLoading} availability={availability} />
      </SectionCard>

      {/* Jejak kumulatif: jangkauan CLP sepanjang waktu */}
      <SectionCard
        icon="bi-globe-asia-australia"
        iconColor="warning"
        eyebrow="Jejak Kumulatif"
        title="Jangkauan CLP Sepanjang Waktu"
      >
        <p className="text-muted small mb-3">
          <i className="bi bi-info-circle me-1"></i>
          Semua angka dihitung dari peserta yang <strong>diterima</strong> (status Aktif) di Praktikum, MSIB, Mandiri, Volunteer — bukan dari semua pendaftar. Sepanjang waktu, bukan per periode.
        </p>

        <div className="row row-cols-2 row-cols-md-4 row-cols-xl-7 g-3">
          <div className="col">
            <ReachTile
              icon="bi-people-fill"
              label="Peserta Diterima"
              value={formatNumber(clpReach?.students)}
              loading={clpReachLoading}
              info="Lolos seleksi dan menjalani program (status Aktif). Pendaftar yang ditolak, mengundurkan diri, atau masih seleksi tidak dihitung. Ini populasi yang sama dengan “Alumni Terdaftar” di HOL Admin, minus segelintir kasus: belum tercatat di Home of Leaders, atau pernah lolos lalu mencoba lagi ke batch lain dan gagal (HOL Admin hanya membaca batch terakhir mereka)."
            />
          </div>
          <div className="col">
            <ReachTile
              icon="bi-person-badge-fill"
              label="Mentor"
              value={formatNumber(clpReach?.mentors)}
              loading={clpReachLoading}
            />
          </div>
          <div className="col">
            <ReachTile
              icon="bi-mortarboard-fill"
              label="Universitas"
              value={formatNumber(clpReach?.universities)}
              loading={clpReachLoading}
            />
          </div>
          <div className="col">
            <ReachTile
              icon="bi-diagram-3-fill"
              label="Program Studi"
              value={formatNumber(clpReach?.majors)}
              loading={clpReachLoading}
            />
          </div>
          <div className="col">
            <ReachTile
              icon="bi-grid-3x3-gap-fill"
              label="Divisi"
              value={formatNumber(clpReach?.divisions)}
              loading={clpReachLoading}
            />
          </div>
          <div className="col">
            <ReachTile
              icon="bi-geo-alt-fill"
              label="Provinsi"
              value={formatNumber(clpReach?.provinces)}
              loading={clpReachLoading}
            />
          </div>
          <div className="col">
            <ReachTile
              icon="bi-building-fill"
              label="Lembaga Mitra"
              value={formatNumber(clpReach?.institutions)}
              loading={clpReachLoading}
              info="Lembaga tempat penempatan peserta yang diterima."
            />
          </div>
        </div>
      </SectionCard>

      {/* Zona 2: Tren Semi-Strategis */}
      <SectionCard
        icon="bi-graph-up-arrow"
        iconColor="success"
        eyebrow="Tren Semi-Strategis"
        title="Pertumbuhan Akun Platform"
      >
        <p className="text-muted small mb-3">
          <i className="bi bi-info-circle me-1"></i>
          Akun = siapa pun yang punya akun ILS, termasuk yang belum pernah lolos program.
        </p>

        <div className="row g-3 mb-4">
          <div className="col-md-4 col-sm-6">
            <StatCard
              label="Total Akun Terdaftar"
              value={formatNumber(totalUsersAllTime)}
              sub={deltaBadge(latestYear?.count_users, 'akun baru')}
              loading={loading.usersTotal}
            />
          </div>
          <div className="col-md-4 col-sm-6">
            <StatCard
              label="Pendaftaran CLP (Semua Status)"
              value={formatNumber(totalClpAllTime)}
              sub={deltaBadge(latestYear?.count_clp_users, 'baru')}
              loading={loading.usersTotal}
              info="Satu orang bisa mendaftar di lebih dari satu batch, jadi ini jumlah pendaftaran, bukan orang. Termasuk yang ditolak, mundur, dan masih seleksi. Yang diterima ada di “Peserta Diterima” pada bagian Jangkauan CLP di atas."
            />
          </div>
          <div className="col-md-4 col-sm-6">
            <StatCard
              label="Rasio Pendaftaran CLP"
              value={`${ratioAllTime}%`}
              sub={<span className="small text-muted">{formatNumber(totalClpAllTime)} pendaftaran dari {formatNumber(totalUsersAllTime)} akun</span>}
              loading={loading.usersTotal}
              info="Rasio dihitung dari total pendaftaran CLP (semua status) dibagi total akun platform."
            />
          </div>
        </div>

        <UsersGrowthChart analytics={analytics} loading={loading.usersTotal} />
      </SectionCard>

      <style>{`
        #cohortDropdown + .dropdown-menu > div::-webkit-scrollbar {
          width: 6px;
        }
        #cohortDropdown + .dropdown-menu > div::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        #cohortDropdown + .dropdown-menu > div::-webkit-scrollbar-thumb {
          background: #ccc;
          border-radius: 10px;
        }
        #cohortDropdown + .dropdown-menu > div::-webkit-scrollbar-thumb:hover {
          background: #999;
        }
        .dropdown-item.active {
          background-color: #0d6efd !important;
        }
        .dropdown-item:hover, .dropdown-item:focus {
          background-color: #e2e6ea;
        }
        .dropdown-item.active:hover, .dropdown-item.active:focus {
          background-color: #0b5ed7 !important;
        }
        #cohortDropdown:hover, .btn-outline-secondary.rounded-circle:hover {
          background-color: #e2e6ea;
          color: #212529;
        }
        .extra-small { font-size: 0.6rem; letter-spacing: 0.5px; }
        .skeleton-block { background: linear-gradient(90deg, #f0f0f0 25%, #f7f7f7 37%, #f0f0f0 63%); background-size: 400% 100%; animation: skeleton-loading 1.4s ease infinite; }
        @keyframes skeleton-loading { 0% { background-position: 100% 50%; } 100% { background-position: 0 50%; } }
        .info-tip-trigger { border: none; background: transparent; padding: 0; line-height: 1; color: #adb5bd; display: inline-flex; align-items: center; font-size: 0.8rem; }
        .info-tip-trigger:hover, .info-tip-trigger:focus-visible { color: #6c757d; }
        .info-tip-popover { position: absolute; top: 100%; left: 0; z-index: 20; width: 240px; margin-top: 6px; padding: 10px 12px; background: #212529; color: #f8f9fa; font-size: 0.7rem; line-height: 1.4; border-radius: 10px; font-weight: 400; text-transform: none; letter-spacing: normal; }
      `}</style>
    </div>
  );
}
